import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getIntuneStatus, getSSOConfig } from '../services/api'

interface IntuneStatus {
  configured: boolean
  connected: boolean
}

interface SSOConfigStatus {
  configured: boolean
  config?: { is_enabled: boolean }
}

/** What the card's badge says about the stored configuration. `null` until the read lands, when
 *  the card keeps its generic "Available" rather than saying something it may have to take back. */
type Badge = { text: string; tone: 'green' | 'amber' | 'gray' } | null

const BADGE_TONES: Record<NonNullable<Badge>['tone'], string> = {
  green: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400',
  amber: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-400',
  gray: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
}

/** The card's action. "Configure" opens an empty setup; "Update" opens one already holding this
 *  tenant's settings. While the read is in flight the button is there but its label is not: a
 *  "Configure" that turns into "Update" a moment later tells a configured tenant it was not. */
const ActionLink: React.FC<{ to: string; configured: boolean | undefined; className: string }> = ({
  to,
  configured,
  className,
}) => (
  <Link to={to} className={className} aria-busy={configured === undefined}>
    {configured === undefined ? (
      <span className="inline-block w-16 h-4 align-middle rounded bg-white/40 animate-pulse" />
    ) : configured ? (
      'Update'
    ) : (
      'Configure'
    )}
  </Link>
)

const StatusBadge: React.FC<{ badge: Badge }> = ({ badge }) => {
  const { text, tone } = badge ?? { text: 'Available', tone: 'green' as const }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${BADGE_TONES[tone]}`}>
      {text}
    </span>
  )
}

const IntegrationsSettings: React.FC = () => {
  // A read that fails (no admin role, say) leaves the card as it always was: "Available" and
  // "Configure". The settings page behind the link is where the actual refusal is explained.
  const intune = useQuery<IntuneStatus>({ queryKey: ['intune-status'], queryFn: getIntuneStatus, retry: false })
  const sso = useQuery<SSOConfigStatus>({ queryKey: ['sso-config'], queryFn: getSSOConfig, retry: false })

  const intuneConfigured = intune.isError ? false : intune.data?.configured
  const ssoConfigured = sso.isError ? false : sso.data?.configured

  const intuneBadge: Badge = !intune.data?.configured
    ? null
    : intune.data.connected
      ? { text: 'Connected', tone: 'green' }
      : { text: 'Not connected', tone: 'amber' }
  const ssoBadge: Badge = !sso.data?.configured
    ? null
    : sso.data.config?.is_enabled
      ? { text: 'Enabled', tone: 'green' }
      : { text: 'Disabled', tone: 'gray' }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-theme-text-primary mb-2">Integrations</h2>
        <p className="text-theme-text-secondary">
          Connect your app catalog with third-party services and MDM platforms.
        </p>
      </div>

      {/* Integration Cards */}
      <div className="grid gap-4">
        {/* Microsoft Intune */}
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.5 2L2 6.5v11L11.5 22 21 17.5v-11L11.5 2zm0 2.18l7.09 3.41v7.82l-7.09 3.41-7.09-3.41V7.59l7.09-3.41z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-theme-text-primary">Microsoft Intune</h3>
                <p className="text-sm text-theme-text-secondary mt-1">
                  Publish Win32 apps directly to your Microsoft Intune tenant
                </p>
                <div className="mt-2">
                  <StatusBadge badge={intuneBadge} />
                </div>
              </div>
            </div>
            <ActionLink
              to="/settings/integrations/intune"
              configured={intuneConfigured}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            />
          </div>
        </div>

        {/* Single Sign-On (SSO) */}
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-theme-text-primary">Single Sign-On (SSO)</h3>
                <p className="text-sm text-theme-text-secondary mt-1">
                  Configure Azure AD, Google Workspace, or Okta SSO with JIT user provisioning
                </p>
                <div className="mt-2">
                  <StatusBadge badge={ssoBadge} />
                </div>
              </div>
            </div>
            <ActionLink
              to="/settings/integrations/sso"
              configured={ssoConfigured}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
            />
          </div>
        </div>

        {/* PSADT - Info Only */}
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6 opacity-75">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9.5 3L4 6.5v11L9.5 21l5.5-3.5v-11L9.5 3zm0 2.18l3.59 2.27v7.1L9.5 16.82l-3.59-2.27v-7.1L9.5 5.18z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-theme-text-primary">PSADT (PowerShell App Deployment Toolkit)</h3>
                <p className="text-sm text-theme-text-secondary mt-1">
                  Built-in support - no configuration needed
                </p>
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                    Built-in
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SCCM - Info Only */}
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6 opacity-75">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/50 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-600 dark:text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-theme-text-primary">SCCM (System Center Configuration Manager)</h3>
                <p className="text-sm text-theme-text-secondary mt-1">
                  Built-in support - export bundles as SCCM-ready packages with import scripts
                </p>
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                    Built-in
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">Need another integration?</h4>
        <p className="text-sm text-blue-700 dark:text-blue-400">
          Contact your administrator to request additional integrations. We're constantly adding new MDM and deployment platform support.
        </p>
      </div>
    </div>
  )
}

export default IntegrationsSettings
